import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Icons } from '../../../components/atoms/SVG';
import { AcceptDeclineForm } from '../../../_lib/Form2';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useDelayed } from '../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { AdditionalPrivilegeItem, BasicPrivilegeItem } from '../PrivilegeConfig.types';
import { BlockType } from '../../../types/api/ElementType';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';

type PrivilegeContainerProps = PropsWithChildren<{
  privilege: BasicPrivilegeItem | AdditionalPrivilegeItem;
  privilegeLabel: string;
}>;

export function BasicPrivilegeContainer({ privilege, privilegeLabel, children }: PrivilegeContainerProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const { active } = useAppContext().editModeInfo;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { refresh } = useRefreshBlock();
  const closeModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the additional privileges block.
    invalidateRefreshableBlock(BlockType.additionalprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  return (
    <div className="privilege-config__basic-privilege-container">
      <BlockBar heading={privilegeLabel} hierarchy={3}>
        {active && privilege.delete
          ? <BlockBarButton
              text={t('actions.delete.headline', {
                name: privilegeLabel,
              })}
              svgSpriteId={Icons.DELETE_TRASHCAN}
              onClick={() => setShowDeleteModal(true)}
            />
          : null}
      </BlockBar>
      {showDeleteModal
        ? (
            <ModalDialog
              closeModal={() => setShowDeleteModal(false)}
              headline={t('actions.delete.headline', {
                name: privilegeLabel,
              })}
              initialFocusIndex={1}
            >
              <AcceptDeclineForm
                messageSuccess={t('actions.delete.status.success')}
                messageSending={t('actions.delete.status.sending')}
                messageFail={t('actions.delete.status.fail')}
                headline={t('actions.delete.question')}
                form={privilege.delete}
                onSuccess={delayedRefreshAndClose}
              />
            </ModalDialog>
          )
        : null}
      {children}
    </div>
  );
}
